import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Trophy,
  Ticket, 
  DollarSign, 
  QrCode,
  TrendingUp,
  CheckCircle,
  Clock,
  Users,
  Calendar,
  MapPin,
  ChevronRight,
  RefreshCw,
  Loader2,
  CreditCard,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Soccer Ball Icon Component
const SoccerBall = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 2C12 2 14.5 5 14.5 8C14.5 11 12 12 12 12C12 12 9.5 11 9.5 8C9.5 5 12 2 12 2Z" fill="currentColor"/>
    <path d="M12 22C12 22 14.5 19 14.5 16C14.5 13 12 12 12 12C12 12 9.5 13 9.5 16C9.5 19 12 22 12 22Z" fill="currentColor"/>
    <path d="M2 12C2 12 5 9.5 8 9.5C11 9.5 12 12 12 12C12 12 11 14.5 8 14.5C5 14.5 2 12 2 12Z" fill="currentColor"/>
    <path d="M22 12C22 12 19 9.5 16 9.5C13 9.5 12 12 12 12C12 12 13 14.5 16 14.5C19 14.5 22 12 22 12Z" fill="currentColor"/>
  </svg>
);

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white/[0.03] rounded-xl border border-white/10 p-4 sm:p-5 hover:border-white/20 transition-all"
  >
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] sm:text-xs uppercase tracking-wide text-gray-500 font-medium mb-1">
          {title}
        </p>
        <p className="text-2xl sm:text-3xl font-bold text-white truncate">
          {value}
        </p>
        <p className="text-xs sm:text-sm text-gray-400 mt-0.5">{subtitle}</p>
      </div>
      <div className={`${color} w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
      </div>
    </div>
  </motion.div>
);

// Zone Analytics Card
const ZoneCard = ({ zone, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
    className="bg-white/[0.02] rounded-xl border border-white/10 p-4 hover:border-emerald-500/30 transition-all"
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-xl">
        {zone.flag}
      </div>
      <div>
        <h3 className="font-semibold text-white text-sm">{zone.name}</h3>
        <p className="text-[10px] text-gray-500">{zone.total_fixtures} fixtures</p>
      </div>
    </div>
    
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">Tickets Sold</span>
        <span className="font-medium text-white">{zone.tickets_sold}</span>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">Revenue</span>
        <span className="font-bold text-emerald-400">D{zone.total_revenue.toLocaleString()}</span>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">Transactions</span>
        <span className="font-medium text-white">{zone.successful_payments}</span>
      </div>
    </div>
  </motion.div>
);

// Top Fixture Card
const TopFixtureCard = ({ fixture, index }) => (
  <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-[#0055FF]/10 flex items-center justify-center text-[#0055FF]">
        <SoccerBall className="w-4 h-4" />
      </div>
      <div>
        <p className="font-medium text-white text-sm">{fixture.home_team} vs {fixture.away_team}</p>
        <p className="text-[10px] text-gray-500">{fixture.zone_name}</p>
      </div>
    </div>
    <div className="text-right">
      <p className="font-bold text-[#0055FF] text-sm">D{fixture.revenue?.toLocaleString() || 0}</p>
      <p className="text-[10px] text-gray-500">{fixture.tickets_sold} sold</p>
    </div>
  </div>
);

// Recent Payment Card
const RecentPaymentCard = ({ payment }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
    <div className="flex items-center gap-2.5 flex-1 min-w-0">
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
        payment.status === 'confirmed' ? 'bg-green-500' : 
        payment.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
      }`} />
      <div className="flex-1 min-w-0">
        <p className="font-mono text-xs text-white truncate">{payment.payment_id}</p>
        <p className="text-[10px] text-gray-500 truncate">{payment.event_name || payment.user_name}</p>
      </div>
    </div>
    <div className="text-right ml-2">
      <p className="font-bold text-white text-sm">D{payment.amount}</p>
      <Badge 
        variant="outline" 
        className={`text-[9px] px-1.5 py-0 ${
          payment.status === 'confirmed' ? 'border-green-500/50 text-green-400' : 
          payment.status === 'pending' ? 'border-yellow-500/50 text-yellow-400' : 'border-red-500/50 text-red-400'
        }`}
      >
        {payment.status}
      </Badge>
    </div>
  </div>
);

export default function DashboardPage() {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchStats();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async (showLoader = false) => {
    try {
      if (showLoader) setRefreshing(true);
      const response = await axios.get(`${API_URL}/api/reports/dashboard`, getAuthHeader());
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchStats(true);
    toast.success('Dashboard refreshed');
  };

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#0055FF] animate-spin" />
        </div>
      </Layout>
    );
  }

  const overviewStats = stats ? [
    {
      title: 'Total Tournaments',
      value: stats.total_tournaments,
      subtitle: `${stats.active_zones} active zones`,
      icon: Trophy,
      color: 'bg-emerald-500'
    },
    {
      title: 'Total Fixtures',
      value: stats.total_fixtures,
      subtitle: `${stats.live_fixtures} live now`,
      icon: SoccerBall,
      color: 'bg-[#0055FF]'
    },
    {
      title: 'Tickets Sold',
      value: stats.total_tickets,
      subtitle: `${stats.tickets_unused} unused`,
      icon: Ticket,
      color: 'bg-purple-500'
    },
    {
      title: 'Total Revenue',
      value: `D${stats.total_revenue?.toLocaleString() || 0}`,
      subtitle: 'Gambian Dalasi',
      icon: DollarSign,
      color: 'bg-amber-500'
    },
    {
      title: 'Total Customers',
      value: stats.total_customers,
      subtitle: 'Registered users',
      icon: Users,
      color: 'bg-pink-500'
    },
    {
      title: 'Today\'s Scans',
      value: stats.today_scans,
      subtitle: 'Validated tickets',
      icon: QrCode,
      color: 'bg-cyan-500'
    }
  ] : [];

  return (
    <Layout title="Dashboard">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-xs sm:text-sm text-gray-500">Platform overview and analytics</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="border-white/20 text-white hover:bg-white/10"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Overview Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-6">
        {overviewStats.map((stat) => (
          <StatCard key={stat.title} {...stat} />
        ))}
      </div>

      {/* Zone Analytics Section */}
      {stats?.zone_analytics?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white">Zone Analytics</h2>
              <p className="text-xs text-gray-500">Revenue by Gambian football zone</p>
            </div>
            <Link to="/admin/tournaments">
              <Button variant="ghost" size="sm" className="text-[#0055FF] hover:text-[#0066FF] hover:bg-white/5">
                Manage Zones <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {stats.zone_analytics.map((zone, index) => (
              <ZoneCard key={zone.id} zone={zone} index={index} />
            ))}
          </div>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
        {/* Ticket Status */}
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm sm:text-base font-semibold text-white flex items-center gap-2">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-[#0055FF]" />
              Ticket Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg border border-white/5">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-300">Used Tickets</span>
                </div>
                <span className="text-xl font-bold text-white">{stats?.tickets_used || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg border border-white/5">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-gray-300">Unused Tickets</span>
                </div>
                <span className="text-xl font-bold text-white">{stats?.tickets_unused || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg border border-white/5">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#0055FF]" />
                  <span className="text-sm text-gray-300">Transactions</span>
                </div>
                <span className="text-xl font-bold text-white">{stats?.total_transactions || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Selling Fixtures */}
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm sm:text-base font-semibold text-white flex items-center gap-2">
                <SoccerBall className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                Top Fixtures
              </CardTitle>
              <Link to="/admin/fixtures">
                <Button variant="ghost" size="sm" className="text-xs text-[#0055FF] hover:text-[#0066FF] hover:bg-white/5 h-7 px-2">
                  View All
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.top_fixtures?.length > 0 ? (
              <div className="space-y-0">
                {stats.top_fixtures.map((fixture, index) => (
                  <TopFixtureCard key={fixture.id} fixture={fixture} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <SoccerBall className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No fixture sales yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Payments */}
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm sm:text-base font-semibold text-white flex items-center gap-2">
                <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                Recent Payments
              </CardTitle>
              <Link to="/admin/payments">
                <Button variant="ghost" size="sm" className="text-xs text-[#0055FF] hover:text-[#0066FF] hover:bg-white/5 h-7 px-2">
                  View All
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.recent_payments?.length > 0 ? (
              <div className="space-y-0">
                {stats.recent_payments.slice(0, 5).map((payment) => (
                  <RecentPaymentCard key={payment.payment_id || payment.id} payment={payment} />
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <CreditCard className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No payments yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Tickets */}
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm sm:text-base font-semibold text-white flex items-center gap-2">
              <Ticket className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" />
              Recent Tickets
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_tickets?.length > 0 ? (
              <div className="space-y-0">
                {stats.recent_tickets.slice(0, 5).map((ticket) => (
                  <div key={ticket.ticket_id || ticket.id} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs text-white truncate">{ticket.ticket_id}</p>
                      <p className="text-[10px] text-gray-500 truncate">{ticket.event_name}</p>
                    </div>
                    <Badge 
                      variant="outline"
                      className={`text-[9px] px-1.5 py-0 ml-2 ${
                        ticket.status === 'UNUSED' ? 'border-green-500/50 text-green-400' : 'border-gray-500/50 text-gray-400'
                      }`}
                    >
                      {ticket.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Ticket className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No tickets yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-white/[0.02] border-white/10 mt-6">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span>Confirmed</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                <span>Pending</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                <span>Failed</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin/tournaments">
                <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 h-8 text-xs">
                  <Trophy className="w-3 h-3 mr-1.5" />
                  Zones
                </Button>
              </Link>
              <Link to="/admin/fixtures">
                <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 h-8 text-xs">
                  <SoccerBall className="w-3 h-3 mr-1.5" />
                  Fixtures
                </Button>
              </Link>
              <Link to="/admin/payments">
                <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 h-8 text-xs">
                  <CreditCard className="w-3 h-3 mr-1.5" />
                  Payments
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </Layout>
  );
}
